import { useEffect, useState, useCallback, useRef } from "react";
import client, { apiErr, API } from "@/lib/api";
import { toast } from "sonner";
import { Spinner } from "@/components/bits";
import { usePaged, Pager, ProgressBar, GoalRing, WeekStrip } from "@/components/Pager";
import { RewriteView } from "@/components/RewriteView";
import { CtrMonitor } from "@/components/CtrMonitor";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { FileEdit, MousePointerClick, Radar, RefreshCw, ExternalLink, AlertTriangle, FileWarning, Layers3, Sparkles, Wand2, GitMerge, Check, CheckCheck, X, PlayCircle, Link2, CalendarDays, Mail, Keyboard, Flame, Pencil, Zap, ShieldCheck, Clock, Eraser, TrendingUp, Target, UserCheck } from "lucide-react";

import { CARDS, TONE, Streak, Kbd, isTyping, ISSUE_TABS, parseCsv } from "@/components/intel/bits";
import { SeoView } from "@/components/intel/SeoView";
import { ArticlesView } from "@/components/intel/ArticlesView";
import { LinksView } from "@/components/intel/LinksView";
import { CalendarView } from "@/components/intel/CalendarView";
import { MergeView } from "@/components/intel/MergeView";
import { MonitorView } from "@/components/intel/MonitorView";

export default function ContentIntel() {
  const { user: authUser } = useAuth();
  const [summary, setSummary] = useState(null);
  const [status, setStatus] = useState(null);
  const [tab, setTab] = useState("stale_meta");
  const [rows, setRows] = useState([]);
  const [clusters, setClusters] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const [counts, setCounts] = useState(null);
  const pg = usePaged(rows, (r) => r.done, 50);

  const loadCounts = useCallback(async () => {
    try { const { data } = await client.get("/content-intel/audit/counts"); setCounts(data); } catch (e) { /* silent */ }
  }, []);

  const loadStatus = useCallback(async () => {
    try { const { data } = await client.get("/content-intel/audit/status"); setStatus(data); if (data.summary) setSummary(data.summary); }
    catch (e) { /* silent */ }
  }, []);
  // Fetch once on mount; keep polling ONLY while an audit is running (avoids constant re-renders / table flicker).
  useEffect(() => { loadStatus(); }, [loadStatus]);
  useEffect(() => { if (!status?.running) return; const t = setInterval(loadStatus, 5000); return () => clearInterval(t); }, [status?.running, loadStatus]);

  const loadList = useCallback(async (issue) => {
    setLoadingList(true);
    try {
      const { data } = await client.get("/content-intel/audit/posts", { params: { issue, limit: 2000 } });
      if (issue === "cannibalization") { setClusters(data.clusters || []); setRows([]); }
      else { setRows(data.posts || []); setClusters([]); }
      loadCounts();
    } catch (e) { toast.error(apiErr(e)); } finally { setLoadingList(false); }
  }, [loadCounts]);
  // Depend on a PRIMITIVE (updated_at) so re-fetched-but-identical status objects don't re-trigger a reload/blink.
  useEffect(() => { if (summary) { pg.reset(); loadList(tab); } }, [tab, summary?.updated_at, loadList]); // eslint-disable-line react-hooks/exhaustive-deps

  const runAudit = async () => {
    try { const { data } = await client.post("/content-intel/audit/run"); toast.success(data.already_running ? "Audit already running" : "Audit started — crawling your posts"); loadStatus(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const setDoneFlags = (ids, done) => { const set = new Set(ids); setRows((r) => r.map((x) => (set.has(x.post_id) ? { ...x, done } : x))); };
  // Session streak (this browser session) + "today" tally from the server (survives refresh).
  const [session, setSession] = useState({ ticked: 0, reviewed: 0 });
  const [today, setToday] = useState(null);
  const [history, setHistory] = useState([]);
  const loadToday = useCallback(async () => {
    try {
      const [{ data }, { data: h }] = await Promise.all([client.get("/content-intel/audit/today"), client.get("/content-intel/audit/history", { params: { days: 7 } })]);
      setToday(data); setHistory(h.days || []);
    } catch (e) { /* silent */ }
  }, []);
  useEffect(() => { loadToday(); }, [loadToday]);
  const bump = (key, n) => setSession((s) => ({ ...s, [key]: Math.max(0, s[key] + n) }));
  // Daily goal ring (per user, persisted server-side). Counts ticks + reviews together.
  const [goal, setGoal] = useState(0);
  const todayTotal = (today?.ticked || 0) + (today?.reviewed || 0);
  useEffect(() => { client.get("/content-intel/audit/goal").then(({ data }) => setGoal(data.goal || 0)).catch(() => {}); }, []);
  const saveGoal = async (g) => {
    try { const { data } = await client.post("/content-intel/audit/goal", { goal: g }); setGoal(data.goal); toast.success(data.goal ? `Daily goal set to ${data.goal}` : "Daily goal cleared"); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const celebrated = useRef(false);
  useEffect(() => {
    if (goal > 0 && todayTotal >= goal && !celebrated.current && today) { celebrated.current = true; toast.success(`Daily goal reached — ${todayTotal} cleared today. Nice work!`, { duration: 6000 }); }
    if (goal > 0 && todayTotal < goal) celebrated.current = false;
  }, [goal, todayTotal, today]);

  const markDoneMany = async (ids) => {
    if (!ids.length) return;
    const first = rows.find((x) => x.post_id === ids[0]);
    setDoneFlags(ids, true); setAnchor(null);
    try {
      if (ids.length === 1) await client.post(`/content-intel/audit/posts/${ids[0]}/done`, { done: true });
      else await client.post("/content-intel/audit/posts/done-bulk", { post_ids: ids, done: true });
      loadCounts(); loadToday(); bump("ticked", ids.length);
      toast(ids.length === 1 ? `Ticked: ${(first?.title || `#${ids[0]}`).slice(0, 60)}` : `Ticked ${ids.length} rows`, {
        id: `tick-${ids[0]}`, duration: 6000,
        action: { label: "Undo", onClick: () => undoDoneMany(ids) },
      });
    } catch (e) { toast.error(apiErr(e)); setDoneFlags(ids, false); }
  };
  const markDone = (post_id) => markDoneMany([post_id]);
  const undoDoneMany = async (ids) => {
    setDoneFlags(ids, false);
    try {
      await client.post("/content-intel/audit/posts/done-bulk", { post_ids: ids, done: false });
      loadCounts(); loadToday(); bump("ticked", -ids.length);
      toast.success(ids.length === 1 ? "Restored — back in the list" : `Restored ${ids.length} rows`, { id: `tick-${ids[0]}`, duration: 2500 });
    } catch (e) { toast.error(apiErr(e)); setDoneFlags(ids, true); }
  };

  // ---- Keyboard ticking: ↑/↓ (or j/k) move, Shift+↑/↓ select a run, Space/Enter ticks the row or the run ----
  const [cursor, setCursor] = useState(0);
  const [anchor, setAnchor] = useState(null);
  const keyboardMode = useRef(false);
  const cursorIdx = Math.min(cursor, Math.max(0, pg.visible.length - 1));
  const range = anchor == null ? null : [Math.min(anchor, cursorIdx), Math.max(anchor, cursorIdx)];
  const inRange = (i) => range && i >= range[0] && i <= range[1];
  useEffect(() => { setCursor(0); setAnchor(null); }, [tab, pg.page]);
  useEffect(() => {
    const el = document.querySelector('[data-testid="issue-row"][data-active="true"]');
    if (el && keyboardMode.current) el.scrollIntoView({ block: "nearest" });
  }, [cursorIdx, pg.visible.length]);
  useEffect(() => {
    // Page fully ticked via keyboard → hop straight to the next unfinished page.
    if (keyboardMode.current && rows.length > 0 && pg.visible.length === 0 && pg.remaining > 0) pg.jumpUnfinished();
  }, [pg.visible.length, pg.remaining, rows.length]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (view !== "health" || tab === "cannibalization") return;
    const onKey = (e) => {
      if (isTyping(e) || e.metaKey || e.ctrlKey || e.altKey) return;
      if (!pg.visible.length) return;
      const move = (d) => { keyboardMode.current = true; if (e.shiftKey) setAnchor((a) => (a == null ? cursorIdx : a)); else setAnchor(null); setCursor((c) => Math.max(0, Math.min(c + d, pg.visible.length - 1))); };
      if (e.key === "ArrowDown" || e.key === "j" || e.key === "J") { e.preventDefault(); move(1); }
      else if (e.key === "ArrowUp" || e.key === "k" || e.key === "K") { e.preventDefault(); move(-1); }
      else if (e.key === " " || e.key === "Enter") {
        e.preventDefault(); keyboardMode.current = true;
        const ids = range ? pg.visible.slice(range[0], range[1] + 1).map((r) => r.post_id) : [pg.visible[cursorIdx].post_id];
        markDoneMany(ids); if (range) setCursor(range[0]);
      }
      else if (e.key === "Escape") { setAnchor(null); }
      else if (e.key === "PageDown" || e.key === "n") { e.preventDefault(); pg.setPage(pg.page + 1); }
      else if (e.key === "PageUp" || e.key === "p") { e.preventDefault(); pg.setPage(pg.page - 1); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }); // no deps on purpose: always sees the latest visible rows / cursor


  const [view, setView] = useState("health");
  // Proposed-count badge for the SEO tab: seeded once here, kept fresh by SeoView while it is mounted.
  const [seoCount, setSeoCount] = useState(null);
  useEffect(() => { client.get("/content-intel/seo/status").then(({ data }) => setSeoCount(data.counts?.proposed)).catch(() => {}); }, []);
  const onSeoStatus = useCallback((st) => setSeoCount(st?.counts?.proposed), []);

  const [traffic, setTraffic] = useState(null);
  const loadTraffic = useCallback(async () => { try { const { data } = await client.get("/content-intel/traffic/status"); setTraffic(data); } catch (e) { /* silent */ } }, []);
  useEffect(() => { loadTraffic(); }, [loadTraffic]);
  const importTraffic = async (file) => {
    try {
      const table = parseCsv(await file.text());
      const header = table[0].map((h) => h.toLowerCase());
      const urlIdx = header.findIndex((h) => h.includes("page") || h.includes("url"));
      const clicksIdx = header.findIndex((h) => h.includes("click"));
      const impIdx = header.findIndex((h) => h.includes("impression"));
      if (urlIdx < 0 || clicksIdx < 0) { toast.error("Couldn't find a Page/URL and Clicks column — export Performance → Pages from Search Console"); return; }
      const rows = table.slice(1).map((c) => ({ page: c[urlIdx] || "", clicks: c[clicksIdx] || 0, impressions: impIdx >= 0 ? c[impIdx] : 0 })).filter((r) => /^https?:\/\//.test(r.page));
      const { data } = await client.post("/content-intel/traffic/import", rows);
      toast.success(`Traffic imported: ${data.matched} of ${data.pages} pages matched to posts (${data.total_clicks.toLocaleString()} clicks)`, { duration: 7000 });
      loadStatus(); loadTraffic(); if (summary) loadList(tab);
    } catch (e) { toast.error(apiErr(e)); }
  };

  const running = status?.running;
  const pct = status?.total ? Math.round((status.processed / status.total) * 100) : 0;

  return (
    <div className="max-w-7xl" data-testid="content-intel-page">
      <p className="text-xs font-mono uppercase tracking-widest text-blue-700">Content Intelligence</p>
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h1 className="text-3xl font-bold text-slate-900" data-testid="content-intel-title">{({ health: "Website Health", seo: "SEO Fixes", articles: "New Articles", links: "Internal Links", calendar: "Content Calendar", merge: "Duplicates", monitor: "Exam Monitor", ctr: "CTR Monitor", rewrite: "Rewrite for 2027" })[view] || "Website Health"}</h1>
        <div className="flex items-center gap-2">
          {traffic?.imported_at && (
            <span data-testid="traffic-status" title={`Imported ${new Date(traffic.imported_at).toLocaleString()} by ${traffic.by}`} className={`inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-2 text-xs font-semibold ${traffic.with_clicks > 0 ? "border-emerald-200 bg-emerald-50 text-emerald-700" : "border-amber-200 bg-amber-50 text-amber-700"}`}>
              <TrendingUp className="h-3.5 w-3.5" /> {traffic.with_clicks > 0 ? `${traffic.with_clicks} posts ranked by ${Number(traffic.total_clicks || 0).toLocaleString()} GSC clicks` : "GSC import present but no posts matched — re-import"}
            </span>
          )}
          <label data-testid="traffic-import-label" className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-600 hover:border-blue-500 cursor-pointer">
            <FileWarning className="h-4 w-4" /> {traffic?.imported_at ? "Re-import GSC CSV" : "Import GSC CSV"}
            <input data-testid="traffic-import-input" type="file" accept=".csv" className="hidden" onChange={(e) => e.target.files[0] && importTraffic(e.target.files[0])} />
          </label>
          <button data-testid="run-audit-btn" onClick={runAudit} disabled={running} className="inline-flex items-center gap-2 rounded-lg border border-blue-600 bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50">
            <Radar className="h-4 w-4" /> {running ? "Auditing…" : "Run full audit"}
          </button>
        </div>
      </div>
      {view === "ctr"
        ? <p className="text-slate-500 mb-5 max-w-2xl">Pages that rank but don't get clicked. Push any of them to priority — Latest-Update refresh plus an in-place update draft — and approve the result in Rewrite 2027.</p>
        : view === "rewrite"
        ? <p className="text-slate-500 mb-5 max-w-2xl">Rewrite whole articles for the 2027 cycle with deep research. AI drafts; a human editor fact-checks and approves before anything replaces a live post.</p>
        : <p className="text-slate-500 mb-5 max-w-2xl">A read-only scan of all posts on your live site. Nothing here writes to WordPress — it surfaces what to update, what's missing, and where SEO can improve. <a href={`${API}/download/health-widget`} className="text-blue-600 font-semibold hover:underline" data-testid="health-widget-download">Download the wp-admin dashboard widget →</a></p>}

      <div className="inline-flex items-center gap-1 rounded-xl border border-slate-200 bg-white p-1 mb-6" data-testid="ci-view-toggle">
        {[["health", "Website Health", Radar, counts?.[tab]?.remaining], ["seo", "SEO Fixes", Wand2, seoCount], ["articles", "New Articles", Sparkles], ["links", "Internal Links", Link2], ["calendar", "Content Calendar", CalendarDays], ["merge", "Duplicates", GitMerge], ["monitor", "Exam Monitor", AlertTriangle], ["ctr", "CTR Monitor", MousePointerClick], ["rewrite", "Rewrite 2027", FileEdit]].map(([id, label, Icon, n]) => (
          <button key={id} data-testid={`view-${id}`} onClick={() => setView(id)}
            className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-semibold transition-colors ${view === id ? "bg-blue-600 text-white" : "text-slate-600 hover:bg-slate-100"}`}>
            <Icon className="h-4 w-4" /> {label}
            {n != null && <span data-testid={`view-count-${id}`} className={`rounded-full px-1.5 py-px text-[10px] tabular-nums ${view === id ? "bg-white/20 text-white" : "bg-slate-200 text-slate-600"}`}>{n}</span>}
          </button>
        ))}
      </div>

      {view === "seo" && <SeoView session={session} today={today} history={history} goal={goal} saveGoal={saveGoal} todayTotal={todayTotal} bump={bump} loadToday={loadToday} onStatus={onSeoStatus} />}
      {view === "links" && <LinksView />}
      {view === "calendar" && <CalendarView />}
      {view === "merge" && <MergeView />}
      {view === "articles" && <ArticlesView />}
      {view === "rewrite" && <RewriteView />}
      {view === "ctr" && <CtrMonitor canManage={authUser?.permissions?.manage} />}
      {view === "monitor" && <MonitorView />}

      {view === "health" && status?.error && <div className="mb-5 rounded-xl border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700">{status.error}</div>}
      {view === "health" && !summary && !running && <div className="rounded-xl border border-dashed border-slate-300 p-10 text-center text-slate-400">No audit yet. Click <b>Run full audit</b> to scan your site.</div>}

      {view === "health" && running && (
        <div className="mb-5 rounded-xl border border-blue-200 bg-blue-50 p-4" data-testid="audit-progress">
          <div className="flex items-center justify-between text-xs text-blue-800 mb-1"><span>Crawling posts…</span><span>{status.processed}/{status.total} ({pct}%)</span></div>
          <div className="h-2 w-full rounded-full bg-blue-100 overflow-hidden"><div className="h-full bg-blue-600 transition-all" style={{ width: `${pct}%` }} /></div>
        </div>
      )}

      {view === "health" && summary && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 mb-8" data-testid="health-cards">
            {CARDS.map((c) => (
              <button key={c.label} disabled={!c.issue} onClick={() => c.issue && setTab(c.issue === "stale_year" ? "stale_year" : c.issue)}
                className={`text-left rounded-xl border p-4 transition-transform ${TONE[c.tone]} ${c.issue ? "hover:-translate-y-0.5 cursor-pointer" : "cursor-default"}`}
                data-testid={`card-${c.key}`}>
                <div className="text-2xl font-bold tabular-nums">{summary[c.key] ?? 0}</div>
                <div className="text-[11px] font-medium mt-0.5 opacity-80">{c.label}</div>
              </button>
            ))}
          </div>

          <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
            <div className="flex items-center gap-1 p-2 border-b border-slate-100 overflow-x-auto">
              {ISSUE_TABS.map((t) => { const c = counts?.[t.id]; const n = t.id === "cannibalization" ? summary?.cannibalization_groups : c?.remaining; return (
                <button key={t.id} data-testid={`tab-${t.id}`} onClick={() => setTab(t.id)}
                  className={`shrink-0 inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors ${tab === t.id ? "bg-blue-600 text-white" : "text-slate-600 hover:bg-slate-100"}`}>
                  {t.label}
                  {n != null && <span data-testid={`tab-count-${t.id}`} className={`rounded-full px-1.5 py-px text-[10px] tabular-nums ${tab === t.id ? "bg-white/20 text-white" : n === 0 ? "bg-emerald-100 text-emerald-700" : "bg-slate-200 text-slate-600"}`}>{n}</span>}
                </button>); })}
            </div>
            {tab !== "cannibalization" && counts?.[tab] && (
              <div className="flex items-center justify-between gap-3 flex-wrap px-3 py-2 border-b border-slate-100 bg-slate-50/60" data-testid="health-progress-bar">
                <div className="flex items-center gap-3 flex-wrap">
                  <ProgressBar done={counts[tab].done} total={counts[tab].total} label="done" testid="health-progress" />
                  <Streak session={session.ticked} today={today?.ticked} noun="ticked" testid="health-streak" />
                  <GoalRing value={todayTotal} goal={goal} onSave={saveGoal} testid="health-goal" />
                  <WeekStrip days={history} goal={goal} testid="health-week" />
                </div>
                <div className="flex items-center gap-3 flex-wrap">
                  {rows.length > 0 && <span data-testid="keyboard-hint" className="hidden xl:inline-flex items-center gap-1.5 text-[11px] text-slate-400"><Keyboard className="h-3.5 w-3.5" /> <Kbd>↑↓</Kbd> move · <Kbd>Shift</Kbd>+<Kbd>↑↓</Kbd> select run · <Kbd>Space</Kbd> tick{range ? ` ${range[1] - range[0] + 1} selected` : ""}</span>}
                  {rows.length > 0 && <Pager pg={pg} testid="health-pager" />}
                </div>
              </div>
            )}
            {loadingList && <div className="p-6"><Spinner label="Loading…" /></div>}

            {!loadingList && tab === "cannibalization" && (
              <div className="divide-y divide-slate-100" data-testid="cannibalization-list">
                {clusters.length === 0 && <div className="p-6 text-center text-slate-400">No near-duplicate title clusters found.</div>}
                {clusters.map((c) => (
                  <div key={c.key} className="p-3">
                    <div className="flex items-center gap-2 text-xs font-semibold text-rose-700 mb-1"><Layers3 className="h-3.5 w-3.5" /> {c.count} posts share this topic</div>
                    <ul className="space-y-1">
                      {c.posts.map((p) => (
                        <li key={p.post_id} className="text-sm text-slate-700 flex items-center gap-2">
                          <a href={p.link} target="_blank" rel="noreferrer" className="hover:text-blue-600 truncate">{p.title}</a>
                          <span className="text-[11px] text-slate-400">#{p.post_id}{p.title_year ? ` · ${p.title_year}` : ""}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            )}

            {!loadingList && tab !== "cannibalization" && (
              <div className="overflow-x-auto max-h-[72vh] overflow-y-auto">
                <table className="w-full text-sm table-fixed" data-testid="issue-table">
                  <thead className="sticky top-0 z-10 bg-slate-50 text-[11px] uppercase tracking-wide text-slate-500">
                    <tr><th className="text-left font-semibold px-3 py-2 w-14">Done</th><th className="text-left font-semibold px-3 py-2 w-[46%]">Post</th><th className="text-left font-semibold px-3 py-2 w-[18%]">Exam · Intent</th><th className="text-left font-semibold px-3 py-2 w-[10%]">Year</th><th className="text-left font-semibold px-3 py-2">Signal</th></tr>
                  </thead>
                  <tbody>
                    {rows.length === 0 && <tr><td colSpan={5} className="p-6 text-center text-slate-400">No posts for this filter.</td></tr>}
                    {rows.length > 0 && pg.visible.length === 0 && (
                      <tr><td colSpan={5} className="p-6 text-center text-emerald-700 bg-emerald-50/60" data-testid="health-page-cleared">
                        <b>Page {pg.page} cleared.</b>{pg.remaining > 0 ? <> {pg.remaining} posts still to go — <button onClick={pg.jumpUnfinished} className="underline font-semibold" data-testid="health-page-cleared-jump">jump to the next unfinished page →</button></> : " Every post in this list is done."}
                      </td></tr>
                    )}
                    {pg.visible.map((r, i) => (
                      <tr key={r.post_id} data-testid="issue-row" data-active={i === cursorIdx ? "true" : undefined} data-selected={inRange(i) ? "true" : undefined}
                        onClick={(e) => { if (e.shiftKey) { setAnchor((a) => (a == null ? cursorIdx : a)); setCursor(i); } else { setAnchor(null); setCursor(i); } }}
                        className={`border-t border-slate-100 align-top cursor-default ${i === cursorIdx ? "bg-blue-50/70 shadow-[inset_3px_0_0_0_#2563eb]" : inRange(i) ? "bg-blue-50" : "hover:bg-slate-50"}`}>
                        <td className="px-3 py-2">
                          <input type="checkbox" data-testid={`issue-done-${r.post_id}`} title="Mark done & remove from list"
                            onChange={() => markDone(r.post_id)}
                            className="h-4 w-4 cursor-pointer accent-emerald-600" />
                        </td>
                        <td className="px-3 py-2">
                          <a href={r.link} target="_blank" rel="noreferrer" className="font-semibold text-slate-800 hover:text-blue-600 inline-flex items-center gap-1"><span className="truncate max-w-[340px]">{r.title}</span><ExternalLink className="h-3 w-3 text-slate-300 shrink-0" /></a>
                          <div className="text-[11px] text-slate-400 font-mono">#{r.post_id} · {r.wordcount} words{r.clicks > 0 && <span className="ml-1 text-emerald-600" data-testid="row-clicks">· {r.clicks.toLocaleString()} clicks</span>}</div>
                        </td>
                        <td className="px-3 py-2 text-xs text-slate-500">{r.exam_group}<div className="text-slate-400">{r.intent}</div></td>
                        <td className="px-3 py-2 text-xs"><span className={r.title_year === 2027 ? "text-emerald-600" : r.title_year ? "text-rose-600 font-semibold" : "text-slate-400"}>{r.title_year || "—"}</span></td>
                        <td className="px-3 py-2 text-xs">
                          {r.stale_meta && <span className="mr-1 inline-flex items-center gap-1 rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-amber-700"><FileWarning className="h-3 w-3" /> meta {r.desc_year || "old"}</span>}
                          {!r.managed && <span className="mr-1 inline-flex items-center rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-slate-500">no block</span>}
                          {r.malformed_year && <span className="mr-1 inline-flex items-center gap-1 rounded-full border border-rose-200 bg-rose-50 px-2 py-0.5 text-rose-700"><AlertTriangle className="h-3 w-3" /> malformed</span>}
                          {r.thin && <span className="mr-1 inline-flex items-center rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-amber-700">thin</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
